"use client";

import React from "react";
import {
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  HStack,
  Text,
  Box,
  useToast,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { agentService } from "../../voice-services/agentService";
import "flag-icons/css/flag-icons.css";

interface LanguageSelectProps {
  initialLanguage: string;
  agentId: string;
}

const languageOptions = [
  { code: "en-US", name: "English (US)", flag: "us" },
  { code: "en-IN", name: "English (India)", flag: "in" },
  { code: "en-GB", name: "English (UK)", flag: "gb" },
  { code: "de-DE", name: "German", flag: "de" },
  { code: "es-ES", name: "Spanish (Spain)", flag: "es" },
  { code: "es-419", name: "Spanish (Latin America)", flag: "es" },
  { code: "hi-IN", name: "Hindi", flag: "in" },
  { code: "ja-JP", name: "Japanese", flag: "jp" },
  { code: "pt-PT", name: "Portuguese (Portugal)", flag: "pt" },
  { code: "pt-BR", name: "Portuguese (Brazil)", flag: "br" },
  { code: "fr-FR", name: "French", flag: "fr" },
  { code: "zh-CN", name: "Chinese (Simplified)", flag: "cn" },
  { code: "ru-RU", name: "Russian", flag: "ru" },
  { code: "it-IT", name: "Italian", flag: "it" },
  { code: "ko-KR", name: "Korean", flag: "kr" },
  { code: "nl-NL", name: "Dutch", flag: "nl" },
  { code: "pl-PL", name: "Polish", flag: "pl" },
  { code: "tr-TR", name: "Turkish", flag: "tr" },
  { code: "vi-VN", name: "Vietnamese", flag: "vn" },
  { code: "multi", name: "Multilingual", flag: "un" },
] as const;

type LanguageCode = (typeof languageOptions)[number]["code"];

export const LanguageSelect: React.FC<LanguageSelectProps> = React.memo(
  ({ initialLanguage, agentId }) => {
    const [selectedLanguage, setSelectedLanguage] =
      React.useState<LanguageCode>(
        (languageOptions.some((l) => l.code === initialLanguage)
          ? initialLanguage
          : "en-US") as LanguageCode,
      );

    const toast = useToast();

    const handleLanguageChange = React.useCallback(
      async (languageCode: LanguageCode) => {
        try {
          await agentService.update(agentId, {
            language: languageCode,
          });

          setSelectedLanguage(languageCode);
          toast({
            title: "Language updated successfully",
            status: "success",
            duration: 2000,
          });
        } catch (error: any) {
          toast({
            title: "Error updating language",
            description:
              error.response?.data?.message || "An unexpected error occurred",
            status: "error",
            duration: 3000,
          });
        }
      },
      [agentId, toast],
    );

    const selectedLang =
      languageOptions.find((lang) => lang.code === selectedLanguage) ||
      languageOptions[0];

    return (
      <Menu>
        <MenuButton
          as={Button}
          variant="outline"
          size="sm"
          rightIcon={<ChevronDownIcon />}
          colorScheme="primary"
          borderColor="primary.400"
          color="gray.800"
          _hover={{ bg: "primary.50", borderColor: "primary.500" }}
          _active={{ bg: "primary.100", borderColor: "primary.600" }}
          _focusVisible={{
            borderColor: "primary.500",
            boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
          }}
        >
          <HStack spacing={2}>
            <Box
              as="span"
              className={`fi fi-${selectedLang.flag}`}
              width="1.5em"
              height="1.5em"
              mr={2}
            />
            <Text color="gray.800">{selectedLang.name}</Text>
          </HStack>
        </MenuButton>

        <MenuList
          maxH="300px"
          overflowY="auto"
          borderColor="primary.200"
          _focusWithin={{
            borderColor: "primary.300",
            boxShadow: "0 0 0 1px var(--chakra-colors-primary-300)",
          }}
          sx={{
            scrollbarWidth: "thin",
            scrollbarColor: "var(--chakra-colors-primary-400) transparent",
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
              borderRadius: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "var(--chakra-colors-primary-400)",
              borderRadius: "8px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "var(--chakra-colors-primary-500)",
            },
          }}
        >
          {languageOptions.map((lang) => (
            <MenuItem
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              _hover={{ bg: "primary.50" }}
              _focus={{ bg: "primary.50" }}
              _active={{ bg: "primary.100" }}
            >
              <HStack spacing={2}>
                <Box
                  as="span"
                  className={`fi fi-${lang.flag}`}
                  width="1.5em"
                  height="1.5em"
                  mr={2}
                />
                <Text>{lang.name}</Text>
              </HStack>
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    );
  },
);

LanguageSelect.displayName = "LanguageSelect";
